import { useState, useEffect } from "react";
import { NewsDetailData } from "../../../pages/NewsDetail/types";
import { getNewsDetailData } from "../../../services/TradingMallService";
import type { INewsItem } from "../../../interfaces/News.interface";

/**
 * Custom hook for fetching news detail data
 * @param newsId The ID of the news article to fetch
 * @returns News data, loading state, and error state
 */
export const useNewsDetail = (newsId: string) => {
  const [newsData, setNewsData] = useState<NewsDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchNewsDetail = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiData: INewsItem | null = await getNewsDetailData(newsId);

        if (!apiData) {
          setNewsData(null);
          return;
        }

        const mapped: NewsDetailData = {
          id: apiData.id,
          image:
            apiData.image ??
            "",
          source: {
            name: apiData.source?.name ?? "ET",
            icon: apiData.source?.icon,
            avatar: apiData.source?.avatar ?? apiData.icon,
          },
          headline: apiData.title,
          timestamp: apiData.timestamp ?? apiData.time ?? "",
          stockTicker: {
            name: apiData.stockName ?? apiData.ticker ?? "",
            change: apiData.change ?? "",
            changeType: apiData.changeType ?? "positive",
          },
          body: apiData.body ?? "",
        };

        setNewsData(mapped);
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to fetch news detail");
        setError(error);
        console.error("Failed to fetch news detail", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsDetail();
  }, [newsId]);

  return { newsData, loading, error };
};

